import i18n from '@/utils/i18n';
import { buildEditsSection } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-special-sections';
import { buildRangeSections } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-column-sections';
import { ColumnId } from '@sales-portal/domain/pricing/screens/customers/types';
import {
  DateRangeFilterSection,
  PillFilterSection,
  PricingFilterSection,
  PricingFilterSectionKind,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { momentWithTimezone } from '@/utils/date';
import { PricingCustomerListRow } from '@sales-portal/domain/pricing/screens/customers/blocs/transformers/use-customer-list-transformer';

const UNASSIGNED_SALES_REP = 'unassigned';

interface BuildCustomerFilterSectionsArgs {
  columnIds: string[];
  editCounts: Record<string, number>;
  rangeColumnConfigs: Record<
    string,
    PricingRangeColumnConfig<PricingCustomerListRow>
  >;
  rows: PricingCustomerListRow[];
}

const buildSalesRepSection = (
  rows: PricingCustomerListRow[],
): PillFilterSection<PricingCustomerListRow> => {
  const reps = new Map<string, string>();
  let hasUnassigned = false;

  for (const row of rows) {
    if (row.salesRepUUID && row.salesRepName) {
      reps.set(row.salesRepUUID, row.salesRepName);
    } else {
      hasUnassigned = true;
    }
  }

  const options = Array.from(reps.entries())
    .sort(([, a], [, b]) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    )
    .map(([value, label]) => ({ label, value }));

  if (hasUnassigned) {
    options.push({
      label: i18n.t('pricing.filters.sales_rep.unassigned'),
      value: UNASSIGNED_SALES_REP,
    });
  }

  return {
    getValues: (row) => [
      row.salesRepUUID && row.salesRepName
        ? row.salesRepUUID
        : UNASSIGNED_SALES_REP,
    ],
    id: ColumnId.SalesRep,
    kind: PricingFilterSectionKind.Pill,
    options,
    singleSelect: false,
    title: i18n.t('pricing.table.header.sales_rep'),
  };
};

const buildLastInvoiceSection =
  (): DateRangeFilterSection<PricingCustomerListRow> => ({
    getDate: (row) =>
      row.lastInvoiceDate ? momentWithTimezone(row.lastInvoiceDate) : null,
    id: ColumnId.LastInvoiceDate,
    kind: PricingFilterSectionKind.DateRange,
    title: i18n.t('pricing.table.header.last_invoice'),
  });

export const buildCustomerFilterSections = ({
  columnIds,
  editCounts,
  rangeColumnConfigs,
  rows,
}: BuildCustomerFilterSectionsArgs): PricingFilterSection<PricingCustomerListRow>[] => {
  const sections: PricingFilterSection<PricingCustomerListRow>[] = [
    buildEditsSection(editCounts, (row) => row.restaurantUUID),
  ];

  for (const columnId of columnIds) {
    if (columnId === ColumnId.SalesRep) {
      sections.push(buildSalesRepSection(rows));
      continue;
    }

    if (columnId === ColumnId.LastInvoiceDate) {
      sections.push(buildLastInvoiceSection());
      continue;
    }

    const config = rangeColumnConfigs[columnId];
    if (!config) {
      continue;
    }

    sections.push(...buildRangeSections(columnId, config, rows));
  }

  return sections;
};
